import './index.css';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { App, ProtectedRoute, ProtectedPlatformRoute, ProtectedAffiliateRoute, RootRedirect } from './App';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { I18nProvider, useT } from './i18n';

// Eager: auth pages (no auth required, small, immediate first paint)
import { LoginPage } from './pages/LoginPage';

// Lazy: every other page. Convert named exports to default.
const lazyNamed = <K extends string>(loader: () => Promise<Record<string, unknown>>, key: K) =>
  lazy(() => loader().then((m) => ({ default: m[key] as React.ComponentType })));

// Signup e login da plataforma saem do bundle principal (budget de 60 kB); LoginPage segue eager.
const SignupPage             = lazyNamed(() => import('./pages/SignupPage'),             'SignupPage');
const PlatformLoginPage      = lazyNamed(() => import('./pages/admin/PlatformLoginPage'), 'PlatformLoginPage');
const AccommodationsPage     = lazyNamed(() => import('./pages/AccommodationsPage'),     'AccommodationsPage');
const AccommodationEditPage  = lazyNamed(() => import('./pages/AccommodationEditPage'),  'AccommodationEditPage');
const StaysPage              = lazyNamed(() => import('./pages/StaysPage'),              'StaysPage');
const StaysCalendarPage      = lazyNamed(() => import('./pages/StaysCalendarPage'),      'StaysCalendarPage');
const HospedagemDashboardPage = lazyNamed(() => import('./pages/HospedagemDashboardPage'), 'HospedagemDashboardPage');
const BillingPage             = lazyNamed(() => import('./pages/BillingPage'),             'BillingPage');
const DashboardPage          = lazyNamed(() => import('./pages/DashboardPage'),          'DashboardPage');
const ExperiencesPage        = lazyNamed(() => import('./pages/ExperiencesPage'),        'ExperiencesPage');
const ExperienceFormPage     = lazyNamed(() => import('./pages/ExperienceFormPage'),     'ExperienceFormPage');
const BookingsPage           = lazyNamed(() => import('./pages/BookingsPage'),           'BookingsPage');
const BookingDetailPage      = lazyNamed(() => import('./pages/BookingDetailPage'),      'BookingDetailPage');
const SchedulingPage         = lazyNamed(() => import('./pages/SchedulingPage'),         'SchedulingPage');
const ContactsPage           = lazyNamed(() => import('./pages/ContactsPage'),           'ContactsPage');
const StaffPage              = lazyNamed(() => import('./pages/StaffPage'),              'StaffPage');
const SettingsPage           = lazyNamed(() => import('./pages/SettingsPage'),           'SettingsPage');
const ReviewsPage            = lazyNamed(() => import('./pages/ReviewsPage'),            'ReviewsPage');
const CouponsPage            = lazyNamed(() => import('./pages/CouponsPage'),            'CouponsPage');
const VouchersPage           = lazyNamed(() => import('./pages/VouchersPage'),           'VouchersPage');
const ProductsPage           = lazyNamed(() => import('./pages/ProductsPage'),           'ProductsPage');
const HousekeepingPage       = lazyNamed(() => import('./pages/HousekeepingPage'),       'HousekeepingPage');
const FinancialPage          = lazyNamed(() => import('./pages/FinancialPage'),          'FinancialPage');
const PricingRulesPage       = lazyNamed(() => import('./pages/PricingRulesPage'),       'PricingRulesPage');
const WaitlistPage           = lazyNamed(() => import('./pages/WaitlistPage'),           'WaitlistPage');
const InboxPage              = lazyNamed(() => import('./pages/InboxPage'),              'InboxPage');
const FollowUpsPage          = lazyNamed(() => import('./pages/FollowUpsPage'),          'FollowUpsPage');
const NpsPage                = lazyNamed(() => import('./pages/NpsPage'),                'NpsPage');
const QuickRepliesPage       = lazyNamed(() => import('./pages/QuickRepliesPage'),       'QuickRepliesPage');
const TagsPage               = lazyNamed(() => import('./pages/TagsPage'),               'TagsPage');
const TriggersPage           = lazyNamed(() => import('./pages/TriggersPage'),           'TriggersPage');
const AdminDashboardPage     = lazyNamed(() => import('./pages/admin/AdminDashboardPage'),     'AdminDashboardPage');
const AdminTenantsPage       = lazyNamed(() => import('./pages/admin/AdminTenantsPage'),       'AdminTenantsPage');
const AdminTenantDetailPage  = lazyNamed(() => import('./pages/admin/AdminTenantDetailPage'),  'AdminTenantDetailPage');
const AdminPlansPage         = lazyNamed(() => import('./pages/admin/AdminPlansPage'),         'AdminPlansPage');
const AdminSubscriptionsPage = lazyNamed(() => import('./pages/admin/AdminSubscriptionsPage'), 'AdminSubscriptionsPage');
const AdminAuditPage         = lazyNamed(() => import('./pages/admin/AdminAuditPage'),         'AdminAuditPage');
const AdminFnrhPage          = lazyNamed(() => import('./pages/admin/AdminFnrhPage'),          'AdminFnrhPage');
const AffiliateSettingsPage    = lazyNamed(() => import('./pages/AffiliateSettingsPage'),    'AffiliateSettingsPage');
const AffiliateProductsPage    = lazyNamed(() => import('./pages/AffiliateProductsPage'),    'AffiliateProductsPage');
const AffiliateRequestsPage    = lazyNamed(() => import('./pages/AffiliateRequestsPage'),    'AffiliateRequestsPage');
const AffiliatePerformancePage = lazyNamed(() => import('./pages/AffiliatePerformancePage'), 'AffiliatePerformancePage');

const AffiliateLoginPage         = lazyNamed(() => import('./pages/AffiliateLoginPage'),         'AffiliateLoginPage');
const AffiliateRegisterPage      = lazyNamed(() => import('./pages/AffiliateRegisterPage'),      'AffiliateRegisterPage');
const AffiliateOnboardingMpPage  = lazyNamed(() => import('./pages/AffiliateOnboardingMpPage'),  'AffiliateOnboardingMpPage');
const IntegrationsMetaCallbackPage = lazyNamed(() => import('./pages/IntegrationsMetaCallbackPage'), 'IntegrationsMetaCallbackPage');
const AffiliateDashboardPage     = lazyNamed(() => import('./pages/AffiliateDashboardPage'),     'AffiliateDashboardPage');
const AffiliateMyProductsPage    = lazyNamed(() => import('./pages/AffiliateMyProductsPage'),    'AffiliateMyProductsPage');
const AffiliateLinksPage         = lazyNamed(() => import('./pages/AffiliateLinksPage'),         'AffiliateLinksPage');
const AffiliateMyCommissionsPage = lazyNamed(() => import('./pages/AffiliateMyCommissionsPage'), 'AffiliateMyCommissionsPage');

function PageFallback() {
  const t = useT();
  return (
    <div className="flex items-center justify-center h-screen text-ink-muted gap-2">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">{t('common.loading')}</span>
    </div>
  );
}

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<App />}>
              <Route index element={<RootRedirect />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignupPage />} />

              <Route element={<ProtectedRoute />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="experiences" element={<ExperiencesPage />} />
                <Route path="experiences/new" element={<ExperienceFormPage />} />
                <Route path="experiences/:id/edit" element={<ExperienceFormPage />} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="bookings/:id" element={<BookingDetailPage />} />
                <Route path="scheduling" element={<SchedulingPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="staff" element={<StaffPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="reviews" element={<ReviewsPage />} />
                <Route path="coupons" element={<CouponsPage />} />
                <Route path="vouchers" element={<VouchersPage />} />
                <Route path="pricing-rules" element={<PricingRulesPage />} />
                <Route path="waitlist" element={<WaitlistPage />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="follow-ups" element={<FollowUpsPage />} />
                <Route path="nps" element={<NpsPage />} />
                <Route path="quick-replies" element={<QuickRepliesPage />} />
                <Route path="tags" element={<TagsPage />} />
                <Route path="triggers" element={<TriggersPage />} />
                <Route path="accommodations" element={<AccommodationsPage />} />
                <Route path="accommodations/:id/edit" element={<AccommodationEditPage />} />
                <Route path="stays" element={<StaysPage />} />
                <Route path="stays/calendar" element={<StaysCalendarPage />} />
                <Route path="hospedagem-dashboard" element={<HospedagemDashboardPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="housekeeping" element={<HousekeepingPage />} />
                <Route path="financeiro" element={<FinancialPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="affiliates/settings" element={<AffiliateSettingsPage />} />
                <Route path="affiliates/products" element={<AffiliateProductsPage />} />
                <Route path="affiliates/requests" element={<AffiliateRequestsPage />} />
                <Route path="affiliates/performance" element={<AffiliatePerformancePage />} />
                <Route path="integrations/meta/callback" element={<IntegrationsMetaCallbackPage />} />
              </Route>

              <Route path="affiliate/login" element={<AffiliateLoginPage />} />
              <Route path="affiliate/register" element={<AffiliateRegisterPage />} />
              <Route path="affiliate" element={<ProtectedAffiliateRoute />}>
                <Route index element={<AffiliateDashboardPage />} />
                <Route path="onboarding/mp" element={<AffiliateOnboardingMpPage />} />
                <Route path="dashboard" element={<AffiliateDashboardPage />} />
                <Route path="products" element={<AffiliateMyProductsPage />} />
                <Route path="links" element={<AffiliateLinksPage />} />
                <Route path="commissions" element={<AffiliateMyCommissionsPage />} />
              </Route>

              <Route path="platform/login" element={<PlatformLoginPage />} />
              <Route path="platform" element={<ProtectedPlatformRoute />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="dashboard" element={<AdminDashboardPage />} />
                <Route path="tenants" element={<AdminTenantsPage />} />
                <Route path="tenants/:id" element={<AdminTenantDetailPage />} />
                <Route path="plans" element={<AdminPlansPage />} />
                <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
                <Route path="audit" element={<AdminAuditPage />} />
                <Route path="fnrh" element={<AdminFnrhPage />} />
              </Route>

              <Route path="*" element={<RootRedirect />} />
            </Route>
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
      </I18nProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
